
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DaysOfWeek flags
 */
class DaysOfWeek {
  /**
   * Initialize flags with a given value
   * @param {DaysOfWeek|number|Int64|UInt64=} value Flags value, defaults is 0
   * @constructor
   */
  constructor (value = 0) {
    if (value instanceof DaysOfWeek) {
      this.value = value.value
    } else {
      this.value = value
    }
  }

  /**
   * Check for the given flags set state
   * @this {!DaysOfWeek}
   * @param {!DaysOfWeek|!number|!Int64|!UInt64} flags Flags
   * @returns {!boolean} Flags set state
   */
  hasFlags (flags) {
    if (flags instanceof DaysOfWeek) {
      flags = flags.value
    }
    return ((this.value & flags) !== 0) && ((this.value & flags) === flags)
  }

  /**
   * Set the given flags
   * @this {!DaysOfWeek}
   * @param {!DaysOfWeek|!number|!Int64|!UInt64} flags Flags
   */
  setFlags (flags) {
    if (flags instanceof DaysOfWeek) {
      flags = flags.value
    }
    this.value |= flags
    return this
  }

  /**
   * Remove the given flags
   * @this {!DaysOfWeek}
   * @param {!DaysOfWeek|!number|!Int64|!UInt64} flags Flags
   */
  removeFlags (flags) {
    if (flags instanceof DaysOfWeek) {
      flags = flags.value
    }
    this.value &= ~flags
    return this
  }

  /**
   * Is this flags equal to other one?
   * @this {!DaysOfWeek}
   * @param {!DaysOfWeek} other Other flags
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DaysOfWeek)) {
      throw new TypeError('Instance of DaysOfWeek is required!')
    }
    return this.value === other.value
  }

  /**
   * Get flags value
   * @this {!DaysOfWeek}
   * @returns {!number|!Int64|!UInt64} Flags value
   */
  valueOf () {
    return this.value
  }

  /**
   * Convert flags to string
   * @this {!DaysOfWeek}
   * @returns {!string} Flags value string
   */
  toString () {
    let result = ''
    let first = true
    if ((this.value & DaysOfWeek.Sunday.value) && ((this.value & DaysOfWeek.Sunday.value) === DaysOfWeek.Sunday.value)) {
      result += (first ? '' : '|') + 'Sunday'
      // noinspection JSUnusedAssignment
      first = false
    }
    if ((this.value & DaysOfWeek.Monday.value) && ((this.value & DaysOfWeek.Monday.value) === DaysOfWeek.Monday.value)) {
      result += (first ? '' : '|') + 'Monday'
      // noinspection JSUnusedAssignment
      first = false
    }
    if ((this.value & DaysOfWeek.Tuesday.value) && ((this.value & DaysOfWeek.Tuesday.value) === DaysOfWeek.Tuesday.value)) {
      result += (first ? '' : '|') + 'Tuesday'
      // noinspection JSUnusedAssignment
      first = false
    }
    if ((this.value & DaysOfWeek.Wednesday.value) && ((this.value & DaysOfWeek.Wednesday.value) === DaysOfWeek.Wednesday.value)) {
      result += (first ? '' : '|') + 'Wednesday'
      // noinspection JSUnusedAssignment
      first = false
    }
    if ((this.value & DaysOfWeek.Thursday.value) && ((this.value & DaysOfWeek.Thursday.value) === DaysOfWeek.Thursday.value)) {
      result += (first ? '' : '|') + 'Thursday'
      // noinspection JSUnusedAssignment
      first = false
    }
    if ((this.value & DaysOfWeek.Friday.value) && ((this.value & DaysOfWeek.Friday.value) === DaysOfWeek.Friday.value)) {
      result += (first ? '' : '|') + 'Friday'
      // noinspection JSUnusedAssignment
      first = false
    }
    if ((this.value & DaysOfWeek.Saturday.value) && ((this.value & DaysOfWeek.Saturday.value) === DaysOfWeek.Saturday.value)) {
      result += (first ? '' : '|') + 'Saturday'
      // noinspection JSUnusedAssignment
      first = false
    }
    return result
  }

  /**
   * Inspect flags
   * @this {!DaysOfWeek}
   * @returns {!string} Flags value string
   */
  [util.inspect.custom] () {
    return this.toString()
  }

  /**
   * Convert flags to JSON
   * @this {!DaysOfWeek}
   * @returns {!number} Flags value for JSON
   */
  toJSON () {
    return this.value
  }

  /**
   * Create flags from number flags representation
   * @param {!number} flags Number flags representation
   * @returns {!DaysOfWeek} Created flags
   */
  static fromFlags (flags) {
    return new DaysOfWeek(flags)
  }

  /**
   * Create flags from object value
   * @param {!number} other Object value
   * @returns {!DaysOfWeek} Created flags
   */
  static fromObject (other) {
    return new DaysOfWeek(other)
  }
}

// noinspection PointlessArithmeticExpressionJS
DaysOfWeek.Sunday = new DaysOfWeek(0x0000000000000001)
// noinspection PointlessArithmeticExpressionJS
DaysOfWeek.Monday = new DaysOfWeek(0x0000000000000002)
// noinspection PointlessArithmeticExpressionJS
DaysOfWeek.Tuesday = new DaysOfWeek(0x0000000000000004)
// noinspection PointlessArithmeticExpressionJS
DaysOfWeek.Wednesday = new DaysOfWeek(0x0000000000000008)
// noinspection PointlessArithmeticExpressionJS
DaysOfWeek.Thursday = new DaysOfWeek(0x0000000000000010)
// noinspection PointlessArithmeticExpressionJS
DaysOfWeek.Friday = new DaysOfWeek(0x0000000000000020)
// noinspection PointlessArithmeticExpressionJS
DaysOfWeek.Saturday = new DaysOfWeek(0x0000000000000040)

export { DaysOfWeek };

/**
 * Fast Binary Encoding DaysOfWeek field model
 */
class FieldModelDaysOfWeek extends fbe.FieldModel {
  /**
   * Get the field size
   * @this {!FieldModelDaysOfWeek}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 8
  }

  /**
   * Get the value
   * @this {!FieldModelDaysOfWeek}
   * @param {DaysOfWeek=} defaults Default value, defaults is new DaysOfWeek()
   * @returns {!DaysOfWeek} Result value
   */
  get (defaults = new DaysOfWeek()) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return defaults
    }

    return new DaysOfWeek(this.readUInt64(this.fbeOffset).toNumber())
  }

  /**
   * Set the value
   * @this {!FieldModelDaysOfWeek}
   * @param {!DaysOfWeek} value Value
   */
  set (value) {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return
    }

    this.writeUInt64(this.fbeOffset, UInt64.fromNumber(value.value))
  }
}

export { FieldModelDaysOfWeek };
